/**
 * appRegistry.js
 * Defines every application available in the H2 Fin portal and
 * which roles can see / access each one.
 *
 * ADDING A NEW TOOL:
 * 1. Add an entry to APP_REGISTRY
 * 2. Add its id to the appropriate role(s) in ROLE_APP_MAP
 * 3. Deploy — the card will appear automatically for authorized users
 *
 * ROLE DETECTION:
 * Roles are resolved from Entra ID security group membership.
 * Replace the group IDs below with the actual Object IDs from
 * Entra ID → Groups → [Group Name] → Object ID.
 */

// ─── Entra Security Group Object IDs ─────────────────────────────────────────
// Replace with real group Object IDs from Entra ID
export const ENTRA_GROUPS = {
  ADMIN:  process.env.REACT_APP_GROUP_ADMIN,
  USER:   process.env.REACT_APP_GROUP_USER,
  BRANCH: process.env.REACT_APP_GROUP_BRANCH,
};
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Portal application registry.
 * Each entry describes one tool card shown in the portal.
 *
 * Fields:
 *   id          — unique slug, used in role mapping
 *   name        — display name on card
 *   tagline     — single-line description shown on card
 *   url         — destination when card is clicked (subdomain or route)
 *   icon        — emoji or can be replaced with SVG component
 *   accentColor — HSL values for card accent stripe (h, s%, l%)
 *   status      — "live" | "beta" | "coming-soon"
 */
export const APP_REGISTRY = [
  {
    id:          "harnesstrack",
    name:        "HarnessTrack",
    tagline:     "CRM & Workflow Management",
    url:         "https://wonderful-bay-0a3ca4a10.1.azurestaticapps.net",   // Update to production Static Web App URL
    icon:        "📊",
    accentColor: "210, 80%, 56%",             // Blue
    status:      "live",
  },
  {
    id:          "harnesspay-pnl",
    name:        "HarnessPay · P&L",
    tagline:     "Revenue, Expenses & Margin Dashboard",
    url:         "https://ashy-beach-0bb467a0f.4.azurestaticapps.net/",      // Update to production Static Web App URL
    icon:        "📈",
    accentColor: "142, 71%, 45%",             // Green
    status:      "live",
  },
  {
    id:          "harnesspay-payroll",
    name:        "HarnessPay · Payroll",
    tagline:     "Compensation, ACH & Pay Runs",
    url:         "https://payroll.h2fin.com",  // Update — currently desktop app during migration
    icon:        "💳",
    accentColor: "271, 70%, 58%",             // Purple
    status:      "beta",
  },
  {
    id:          "sec204",
    name:        "Section 204",
    tagline:     "RMD Modeling · Term-Certain SPIA Analysis",
    url:         "https://204.h2fin.com",   // Update to production Static Web App URL
    icon:        "📐",
    accentColor: "36, 90%, 52%",              // Amber
    status:      "live",
  },
  {
    id:          "exitplanning",
    name:        "Exit Planning",
    tagline:     "Practice Succession & Exit Modeling",
    url:         "https://exit.h2fin.com",     // Update to production Static Web App URL
    icon:        "🚀",
    accentColor: "0, 72%, 51%",               // Red
    status:      "live",
  },
];

/**
 * Role → app access mapping.
 * Lists the app IDs each role is authorized to see and access.
 * Apps not listed for a role are completely absent from the portal (not disabled).
 */
export const ROLE_APP_MAP = {
  admin: [
    "harnesstrack",
    "harnesspay-pnl",
    "harnesspay-payroll",
    "sec204",
    "exitplanning",
  ],
  user: [
    "harnesstrack",
    "sec204",
    "exitplanning",
  ],
  branch: [
    "harnesstrack",   // Read-only enforced at HarnessTrack API layer
  ],
};

/**
 * Resolves a portal role from a list of Entra group IDs
 * returned by the Graph /me/memberOf endpoint.
 *
 * Priority: admin > user > branch > null (no access)
 *
 * @param {string[]} groupIds — array of group Object IDs from Graph API
 * @returns {"admin" | "user" | "branch" | null}
 */
export function resolveRole(groupIds = []) {
  if (groupIds.includes(ENTRA_GROUPS.ADMIN))  return "admin";
  if (groupIds.includes(ENTRA_GROUPS.USER))   return "user";
  if (groupIds.includes(ENTRA_GROUPS.BRANCH)) return "branch";
  return null;
}

/**
 * Returns the subset of APP_REGISTRY entries the given role may access.
 *
 * @param {"admin" | "user" | "branch" | null} role
 * @returns {Array} filtered app registry entries
 */
export function getAuthorizedApps(role) {
  if (!role) return [];
  const allowedIds = ROLE_APP_MAP[role] ?? [];
  return APP_REGISTRY.filter(app => allowedIds.includes(app.id));
}
